import type { Product } from '../api/productApi';

/**
 * The one stock rule, shared by the Sale, Return and Purchase forms:
 *
 *      stock = (opening stock + bought + returned) - sold
 *
 * counted from `STOCK_COUNT_START_DATE` (1 July 2026) on the server. The server
 * writes the result of that count onto the product as `computedStock`, so the
 * forms never re-derive it — they read the same number the reports read.
 *
 * `currentStock` is the raw movement ledger and is only a fallback for products
 * that have not been recalculated yet (sale entries there are negative, so
 * summing it yields the same figure).
 */
export const getProductStock = (
    product: Pick<Product, 'computedStock' | 'currentStock'> | undefined | null,
    unit?: string
): number => {
    if (!product) return 0;

    const computed = product.computedStock;
    if (computed && computed.length > 0) {
        return computed
            .filter(entry => !unit || entry.unit === unit)
            .reduce((sum, entry) => sum + (Number(entry.qty) || 0), 0);
    }

    return (product.currentStock || [])
        .filter(entry => !unit || entry.unit === unit)
        .reduce((sum, entry) => sum + (Number(entry.qty) || 0), 0);
};

/**
 * Stock of one unit (or of every unit when none is given) out of a live
 * `{ [unit]: qty }` answer from `GET /products/:id/current-stock`.
 */
export const sumStockByUnit = (
    byUnit: Record<string, number> | undefined | null,
    unit?: string
): number => {
    if (!byUnit) return 0;
    if (unit) return Number(byUnit[unit]) || 0;
    return Object.values(byUnit).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
};

/** Stock of every unit of a product, keyed by unit. */
export const getProductStockByUnit = (
    product: Pick<Product, 'computedStock' | 'currentStock'> | undefined | null
): Record<string, number> => {
    if (!product) return {};
    const entries = product.computedStock?.length
        ? product.computedStock
        : (product.currentStock || []);
    return entries.reduce((acc, entry) => {
        acc[entry.unit] = (acc[entry.unit] || 0) + (Number(entry.qty) || 0);
        return acc;
    }, {} as Record<string, number>);
};

/**
 * Stock left after this invoice line is applied.
 * A sale takes qty out; a purchase or a return puts it back in.
 */
export const getStockAfterLine = (
    stock: number,
    qty: number,
    type: 'sale' | 'return' | 'purchase'
): number => (type === 'sale' ? stock - (qty || 0) : stock + (qty || 0));
